/**
 * Sandbox Instance Sizing
 *
 * Closed preset enum of EC2 sizes a template can run on, plus a per-size
 * resource spec consumed by provisioning, compose generation, and prompts.
 *
 * Single source of truth for: launch-template instance type override,
 * docker compose mem_limit/cpus per role, prompt-template variables.
 *
 * @module types/sandbox-instance-size
 */

export const INSTANCE_SIZES = ['small', 'medium', 'large', 'xlarge', '2xlarge'] as const;
export type InstanceSize = (typeof INSTANCE_SIZES)[number];

export const DEFAULT_INSTANCE_SIZE: InstanceSize = 'medium';

export interface InstanceSizeSpec {
  /** EC2 instance type for `RunInstancesCommand` override. */
  ec2InstanceType: string;
  vcpu: number;
  memoryMiB: number;
  /**
   * Per-role memory caps (MiB) consumed by `resourceLimitsFor()`. Must cover
   * every key in compose-helpers.ts STATIC_LIMITS.
   *
   * Sum × per-role multiplicity should fit within `memoryMiB × 1.5` to keep
   * swap pressure bounded. See INSTANCE_SIZE_SUM_LIMIT_RATIO.
   */
  agentCapMiB: number;
  appSoftCapMiB: number;
  proxyCapMiB: number;
  emulationCapMiB: number;
  storageCapMiB: number;
  /**
   * Per-size hard CPU quota (cgroup `cpus`) for the user app tier. Other
   * roles (agent, proxy, emulation, storage) keep fixed CPU caps from
   * STATIC_LIMITS — their workloads don't scale with host vCPU. App is the
   * only role that sees a real benefit from a larger instance, so it's the
   * only one keyed here.
   *
   * Formula: max('1.0', vcpu - 1.5) — reserve 1.5 vCPU for the agent.
   * Stored as a string so docker compose receives `'1.0'` (not `'1'`),
   * matching the format of STATIC_LIMITS.cpus values.
   */
  appCpus: string;
}

/** Cap on (sum of role caps) ÷ memoryMiB — anything above this swaps too hard. */
export const INSTANCE_SIZE_SUM_LIMIT_RATIO = 1.5;

export const INSTANCE_SIZE_SPEC: Record<InstanceSize, InstanceSizeSpec> = {
  small: {
    ec2InstanceType: 't3.small',
    vcpu: 2,
    memoryMiB: 2048,
    // 2 GiB instance — agent capped at min(2048, 0.4 × 2048) = 819
    agentCapMiB: 819,
    appSoftCapMiB: 1024,
    proxyCapMiB: 256,
    emulationCapMiB: 128,
    storageCapMiB: 256,
    appCpus: '1.0',
  },
  medium: {
    ec2InstanceType: 't3.medium',
    vcpu: 2,
    memoryMiB: 4096,
    agentCapMiB: 1536,
    appSoftCapMiB: 1536,
    proxyCapMiB: 256,
    emulationCapMiB: 128,
    storageCapMiB: 256,
    appCpus: '1.0',
  },
  large: {
    ec2InstanceType: 't3.large',
    vcpu: 2,
    memoryMiB: 8192,
    agentCapMiB: 2048,
    appSoftCapMiB: 5120,
    proxyCapMiB: 256,
    emulationCapMiB: 128,
    storageCapMiB: 512,
    // t3.large is memory-only — same 2 vCPU as medium.
    appCpus: '1.0',
  },
  xlarge: {
    ec2InstanceType: 't3.xlarge',
    vcpu: 4,
    memoryMiB: 16384,
    agentCapMiB: 2048,
    appSoftCapMiB: 12288,
    proxyCapMiB: 256,
    emulationCapMiB: 256,
    storageCapMiB: 512,
    appCpus: '2.5',
  },
  '2xlarge': {
    ec2InstanceType: 't3.2xlarge',
    vcpu: 8,
    memoryMiB: 32768,
    agentCapMiB: 2048,
    appSoftCapMiB: 28672,
    proxyCapMiB: 512,
    emulationCapMiB: 512,
    storageCapMiB: 1024,
    appCpus: '6.5',
  },
};

const INSTANCE_SIZE_SET = new Set<string>(INSTANCE_SIZES);

/**
 * Narrowing helper for the repo boundary. Returns null on unknown input so
 * callers can surface a typed user error without `as never` / `as any`.
 */
export function parseInstanceSize(s: string | null | undefined): InstanceSize | null {
  return s && INSTANCE_SIZE_SET.has(s) ? (s as InstanceSize) : null;
}

const RECOMMENDATION_MAP: Record<string, InstanceSize> = {
  // Exact preset names
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge',
  '2xlarge': '2xlarge',
  // Common AWS instance types the authoring agent may emit
  't3.nano': 'small',
  't3.micro': 'small',
  't3.small': 'small',
  't3.medium': 'medium',
  't3.large': 'large',
  't3.xlarge': 'xlarge',
  't3.2xlarge': '2xlarge',
  't2.small': 'small',
  't2.medium': 'medium',
  't2.large': 'large',
  't2.xlarge': 'xlarge',
  't2.2xlarge': '2xlarge',
};

/**
 * Maps a free-form `recommendedInstanceType` string (often produced by the
 * authoring agent before this enum existed) to the closest preset.
 *
 * Strategy: exact match against known names first, then fall back to nearest
 * memoryMiB. Ties broken toward cheaper (lower `memoryMiB`).
 */
export function inferInstanceSizeFromRecommendation(
  s: string | null | undefined
): InstanceSize | null {
  if (!s) return null;
  const trimmed = s.trim().toLowerCase();
  if (!trimmed) return null;
  const exact = RECOMMENDATION_MAP[trimmed];
  if (exact) return exact;

  // Last-resort heuristic: parse a memory hint like "4 GiB" / "8GB" / "2g".
  const match = trimmed.match(/(\d+(?:\.\d+)?)\s*(g|gi|gb|gib|m|mi|mb|mib)\b/);
  if (!match) return null;
  const value = Number.parseFloat(match[1]!);
  if (!Number.isFinite(value)) return null;
  const memMiB = match[2]!.startsWith('g') ? value * 1024 : value;

  let best: InstanceSize | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const size of INSTANCE_SIZES) {
    const delta = Math.abs(INSTANCE_SIZE_SPEC[size].memoryMiB - memMiB);
    if (delta < bestDelta) {
      best = size;
      bestDelta = delta;
    }
  }
  return best;
}
